/**
 * The primary rail.
 *
 * Two states, one layout: 232px with labels, 56px with icons and tooltips. The
 * active item is marked by a 2px gold rail plus a selected ground — never a
 * filled pill, which would out-shout the content it points at.
 *
 * Every count on this rail is a real server total. Nothing here is decorative.
 */

import {
  BarChart3,
  Bookmark,
  Briefcase,
  Bug,
  Building2,
  CalendarClock,
  CheckSquare,
  Contact,
  Filter,
  Flame,
  Globe,
  Inbox,
  Kanban,
  LifeBuoy,
  ListChecks,
  Plus,
  Settings,
  Star,
  Timer,
  Trophy,
  Users,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import type { ComponentType, CSSProperties, ReactNode } from 'react';

import { UserMenu } from '@/components/app/UserMenu';
import { useOnboardingProgress } from '@/features/onboarding/useOnboarding';
import { IconButton, ScrollArea, Skeleton, Tooltip } from '@/components/ui';
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  dialogOverlayClass,
} from '@/components/ui/Dialog';
import { compactNumber } from '@/lib/format';
import { ROUTES } from '@/lib/constants';
import { useCreate } from '@/lib/create';
import {
  useInquiries,
  useSavedViews,
  useTasks,
  useUnverifiedBugCount,
  useViewCounts,
} from '@/lib/queries';
import type { SavedView, SessionResponse } from '@shared/types';

/** The only icon shape the shell needs — every lucide glyph satisfies it. */
export type ShellIcon = ComponentType<{
  className?: string;
  style?: CSSProperties;
  // `number | string`, so a plain lucide icon (`IconComponent` in
  // `lib/constants`, whose props come from `SVGProps`) satisfies this without a
  // cast. The command palette renders `CREATE_ACTIONS`' icons through here.
  strokeWidth?: number | string;
  'aria-hidden'?: boolean | 'true' | 'false';
}>;

/* ────────────────────────────────────────────────────────────────────────────
   Brand
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The AlphaForm monogram. The navy ground is deliberately theme-invariant — a
 * mark that changes colour with the UI theme is not a mark.
 */
export function AlphaMark({ size = 26, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      /* The one declared non-create use of the brand gold left in the product,
         and the only one that can be: this IS the brand. It is not a control, it
         has no state, and it is the same mark on the marketing site. The
         nav-current rail used to carry a declaration too and no longer does —
         see `NavRow`. `scripts/invariants-check.mjs` reads this attribute. */
      data-accent-role="brand"
      className={className}
    >
      <rect width="32" height="32" rx="7" fill="var(--navy-850)" />
      <path d="M16 5.4 26.6 26.6h-5.2L16 15.2 10.6 26.6H5.4Z" fill="var(--accent)" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Nav model
   ──────────────────────────────────────────────────────────────────────────── */

type CountKind = 'unread' | 'overdue';

export interface NavEntry {
  id: string;
  label: string;
  to: string;
  icon: ShellIcon;
  count?: CountKind;
}

/** Shared with the command palette, so navigation is described in one place. */
export const PRIMARY_NAV: NavEntry[] = [
  {
    id: 'inbox',
    label: 'Inbox',
    to: ROUTES.inbox,
    icon: Inbox,
    count: 'unread',
  },
  { id: 'board', label: 'Board', to: ROUTES.board, icon: Kanban },
  { id: 'contacts', label: 'Contacts', to: ROUTES.contacts, icon: Contact },
  {
    id: 'companies',
    label: 'Companies',
    to: ROUTES.companies,
    icon: Building2,
  },
  {
    id: 'tasks',
    label: 'Tasks',
    to: ROUTES.tasks,
    icon: CheckSquare,
    count: 'overdue',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    to: ROUTES.analytics,
    icon: BarChart3,
  },
];

/** Saved views carry an icon *name*; this is the vocabulary they may use. */
const VIEW_ICONS: Record<string, ShellIcon> = {
  inbox: Inbox,
  flame: Flame,
  star: Star,
  timer: Timer,
  clock: CalendarClock,
  users: Users,
  contact: Contact,
  building: Building2,
  company: Building2,
  filter: Filter,
  globe: Globe,
  trophy: Trophy,
  tasks: ListChecks,
  briefcase: Briefcase,
  bookmark: Bookmark,
};

export function viewIcon(name: string | null): ShellIcon {
  if (name && name in VIEW_ICONS) return VIEW_ICONS[name]!;
  return Bookmark;
}

export const VIEW_ROUTE: Record<SavedView['entity'], string> = {
  inquiries: ROUTES.inbox,
  contacts: ROUTES.contacts,
  companies: ROUTES.companies,
  tasks: ROUTES.tasks,
};

/** What one view of each entity is called, for the scoped empty line. */
const VIEW_NOUN: Record<SavedView['entity'], string> = {
  inquiries: 'inquiry',
  contacts: 'contact',
  companies: 'company',
  tasks: 'task',
};

/**
 * Which surfaces can actually *make* a view.
 *
 * The well's copy and its `+` are both promises, and a promise is scoped to the
 * screen that can keep it: only the inquiry surfaces (Inbox and the Board that
 * projects it) carry a Save-view flow today. Add an entity here the same commit
 * a save control lands on its list, not before.
 */
const CAN_SAVE_VIEW: Record<SavedView['entity'], boolean> = {
  inquiries: true,
  contacts: false,
  companies: false,
  tasks: false,
};

export function viewHref(view: SavedView): string {
  return `${VIEW_ROUTE[view.entity]}?view=${encodeURIComponent(view.id)}`;
}

/* ────────────────────────────────────────────────────────────────────────────
   Counts
   ──────────────────────────────────────────────────────────────────────────── */

/** Untriaged, still-open inquiries — what an inbox badge has always meant. */
export function useUnreadCount(): number | undefined {
  const { data } = useInquiries({
    limit: 1,
    filters: {
      combinator: 'and',
      conditions: [
        {
          id: 'stageKey~in',
          field: 'stageKey',
          operator: 'in',
          value: ['new'],
        },
        { id: 'status~in', field: 'status', operator: 'in', value: ['open'] },
      ],
    },
  });
  return data?.total;
}

/**
 * Overdue tasks: still open, and past their due date.
 *
 * This asks the server to do the counting — `filters` narrows the set and
 * `total` comes back as the real aggregate, so one row on the wire answers the
 * question. Scanning the head of the list instead would undercount the moment
 * the workspace has more overdue work than the scan window, and would silently
 * mix in completed rows.
 *
 * The `lte` bound is bucketed to the minute so the query key is stable across
 * renders — a raw `Date.now()` would mint a new key (and a new request) on every
 * single render.
 */
const OVERDUE_BUCKET_MS = 60_000;

export function useOverdueCount(): number | undefined {
  const asOf = new Date(
    Math.floor(Date.now() / OVERDUE_BUCKET_MS) * OVERDUE_BUCKET_MS,
  ).toISOString();

  const { data } = useTasks({
    limit: 1,
    filters: {
      combinator: 'and',
      conditions: [
        { id: 'done', field: 'isCompleted', operator: 'is', value: false },
        { id: 'due-to', field: 'dueAt', operator: 'lte', value: asOf },
      ],
    },
  });

  return data?.total;
}

/**
 * One batched request behind one cache key, shared with the inbox's view rail.
 * Four `limit: 1` list queries per saved view — three of them disabled — was
 * a dozen round trips to label a rail that fits on one screen.
 */
function useViewCount(view: SavedView): number | undefined {
  const { data } = useViewCounts();
  return data?.[view.id];
}

/**
 * One number, one formatting rule.
 *
 * This rail and the inbox's view tabs label the *same* saved views from the
 * *same* request (`useViewCounts`), and they disagreed on screen: the tab strip
 * printed 156 while the rail printed 99+ for it, so the product looked like it
 * held two different figures for one view. Grouped digits, never truncated,
 * never compacted — the same string the tab strip renders.
 */
function formatCount(value: number): string {
  return compactNumber(value, { compact: false });
}

/* ────────────────────────────────────────────────────────────────────────────
   Rows
   ──────────────────────────────────────────────────────────────────────────── */

const ROW_BASE =
  'group relative flex items-center rounded-[var(--radius-sm)] text-base ' +
  // The transition names the three properties it means. Tailwind's colour
  // shorthand also carries `outline-color` — the ring — and the ring was
  // measured arriving in the row's own --text-secondary and settling on --focus
  // only ~320ms later. Naming the properties leaves the ring out of the
  // interpolation, so it lands on the same frame focus does.
  'transition-[color,background-color,border-color] duration-[var(--dur-fast)] ease-[var(--ease-out)] ' +
  // The ring colour is set unconditionally, *not* under `focus-visible:`, and it
  // names its property (`[color:…]`), so there is nothing to interpolate even if
  // a future transition list picks outline-color back up. It paints nothing
  // until `outline-style` turns on.
  // `scripts/focus-ring-check.mjs` fails the build if that ever regresses.
  'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]';

const ROW_IDLE =
  'text-secondary hover:bg-[var(--surface-hover)] hover:text-primary active:bg-[var(--surface-active)]';

const ROW_ACTIVE = 'bg-[var(--surface-selected)] font-medium text-primary';

interface NavRowProps {
  to: string;
  icon: ShellIcon;
  label: string;
  collapsed: boolean;
  /**
   * Computed by the caller rather than by `NavLink`: a saved view lives at the
   * same pathname as its section, so only the search string tells them apart.
   */
  active: boolean;
  count?: number | undefined;
  /**
   * An EXCEPTION count — work waiting on you — as opposed to the size of a
   * collection. The two section badges (untriaged, overdue) are exceptions and
   * raise their voice; every saved view's badge is a total and does not.
   */
  urgent?: boolean;
  countNoun?: string;
  /**
   * Collapsed, only the two counts that mean "there is work waiting" get a
   * badge. Everything else would turn a 56px rail into a wall of numbers; the
   * tooltip still carries the figure.
   */
  badgeWhenCollapsed?: boolean;
  /**
   * A saved view's size is a fact about the view, so `0` is a real answer and is
   * printed — the inbox's tab strip prints it too. A section badge means "there
   * is work waiting", so zero is silence.
   */
  showZero?: boolean;
}

function NavRow({
  to,
  icon: Icon,
  label,
  collapsed,
  active,
  count,
  urgent,
  countNoun,
  badgeWhenCollapsed = false,
  showZero = false,
}: NavRowProps) {
  const badge = typeof count === 'number' && (count > 0 || showZero) ? formatCount(count) : null;
  // Raw `--danger` measured 4.46:1 on `--canvas` in the light theme at 11px.
  // Pulling a sixth of the theme's own ink into the hue takes it to 5.9:1 and
  // leaves it unmistakably red.
  const countStyle = urgent
    ? { color: 'color-mix(in srgb, var(--danger) 84%, var(--text-primary))' }
    : undefined;
  const countTone = urgent ? '' : 'text-muted';

  const row = (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={[
        ROW_BASE,
        collapsed ? 'h-9 w-9 justify-center' : 'h-[30px] gap-2 px-2',
        active ? ROW_ACTIVE : ROW_IDLE,
      ].join(' ')}
    >
      {active ? (
        <span
          aria-hidden="true"
          /* `--focus`, not `--accent`.
             This rail is a LOCATION marker, and it used to be the product's one
             declared exemption from the gold rule. An exemption is still a
             second meaning: on /companies the same hue was carrying the create
             button at [1302,108] and this 2px bar at [8,157] at the same time,
             so the brand mark meant "make one" and "you are here" in one glance.
             `--focus` is the token that already means "the thing the keyboard
             and the reader are on", it clears 3:1 on the rail's ground in both
             themes, and it leaves gold spent on create alone.
             `scripts/invariants-check.mjs` sweeps every list surface for
             `--accent`/`--accent-text` in colour, background, stroke and fill,
             and fails anything that is not the create control or a declared
             `data-accent-role`. */
          className="absolute left-0 w-[2px] rounded-[var(--radius-full)] bg-[var(--focus)]"
          style={{ top: 'var(--space-1)', bottom: 'var(--space-1)' }}
        />
      ) : null}

      <Icon
        className={[
          'size-4 shrink-0 transition-[color] ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          active ? 'text-primary' : 'text-faint group-hover:text-secondary',
        ].join(' ')}
        strokeWidth={1.75}
        aria-hidden="true"
      />

      {collapsed ? (
        badge && badgeWhenCollapsed ? (
          <span
            aria-hidden="true"
            className={[
              'tnum absolute -right-1 -top-1 grid h-[15px] min-w-[15px] place-items-center',
              'rounded-[var(--radius-full)] px-[3px] text-[length:var(--text-2xs)] font-medium',
              'ring-1 ring-[var(--canvas)]',
              urgent ? 'bg-[var(--danger-soft)]' : 'bg-[var(--surface-3)] text-secondary',
            ].join(' ')}
            style={countStyle}
          >
            {badge}
          </span>
        ) : null
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {badge ? (
            <span className={`tnum shrink-0 text-xs ${countTone}`} style={countStyle}>
              <span className="sr-only">{countNoun ? `${countNoun}: ` : ''}</span>
              {badge}
            </span>
          ) : null}
        </>
      )}
    </Link>
  );

  if (!collapsed) return row;

  return (
    <Tooltip
      side="right"
      sideOffset={10}
      content={
        <span className="flex items-center gap-2">
          {label}
          {badge ? <span className={`tnum ${countTone}`}>{badge}</span> : null}
        </span>
      }
    >
      {row}
    </Tooltip>
  );
}

function ViewRow({
  view,
  collapsed,
  active,
}: {
  view: SavedView;
  collapsed: boolean;
  active: boolean;
}) {
  const count = useViewCount(view);
  return (
    <NavRow
      to={viewHref(view)}
      icon={viewIcon(view.icon)}
      label={view.name}
      collapsed={collapsed}
      active={active}
      count={count}
      countNoun={view.entity}
      showZero
    />
  );
}

function SectionLabel({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex h-6 items-center justify-between pl-2 pr-0.5">
      <span className="eyebrow truncate">{children}</span>
      {action}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Sidebar
   ──────────────────────────────────────────────────────────────────────────── */

export interface SidebarProps {
  collapsed: boolean;
  session: SessionResponse | undefined;
}

export function Sidebar({ collapsed, session }: SidebarProps) {
  const create = useCreate();
  const location = useLocation();
  const unread = useUnreadCount();
  const overdue = useOverdueCount();
  const onboarding = useOnboardingProgress();
  // Held unconditionally: the server answers 0 for a non-admin rather than 403,
  // so the rail never has to branch on role to decide whether to ask.
  const unverifiedBugs = useUnverifiedBugCount();
  const views = useSavedViews();
  const savedViews = views.data ?? [];

  // A saved view and its section share a pathname, so exactly one of them lights
  // up: the view when `?view=` names it, the section otherwise.
  const activeView = new URLSearchParams(location.search).get('view');
  const onPath = (to: string): boolean =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  /**
   * The object the surface on screen is about — the Board is inquiries under a
   * different projection, a record detail belongs to its own list.
   */
  const surfaceEntity: SavedView['entity'] | null = onPath(ROUTES.board)
    ? 'inquiries'
    : ((Object.keys(VIEW_ROUTE) as SavedView['entity'][]).find((entity) =>
        onPath(VIEW_ROUTE[entity]),
      ) ?? null);

  /**
   * Views are scoped to that object.
   *
   * Every seeded view counts inquiries ("SLA at risk inquiries: 47"), and
   * unscoped they sat directly under the highlighted Contacts item: 250px of
   * rail, the largest cluster of numerals on the screen, all of it about a
   * different collection than the table being read. A saved view is a filtered
   * list of one entity, so it belongs to that entity's surface.
   */
  const scopedViews = surfaceEntity
    ? savedViews.filter((view) => view.entity === surfaceEntity)
    : savedViews;
  const viewNoun = surfaceEntity ? VIEW_NOUN[surfaceEntity] : 'saved';
  const canSaveView = surfaceEntity === null || CAN_SAVE_VIEW[surfaceEntity];

  /**
   * The well only exists where it can hold something.
   *
   * The previous round removed the instruction that could not be followed but
   * left the section header above it, so `/tasks` printed a "VIEWS" eyebrow and
   * "Saved task views appear here." — 48px of rail, permanently, for a
   * collection with no create path anywhere in the product and therefore no way
   * of ever being non-empty. A heading for an empty set nobody can fill is
   * chrome, not navigation: on a surface that can neither save a view nor show
   * one, the block is not rendered at all.
   */
  const showViews = canSaveView || scopedViews.length > 0;

  return (
    <aside
      aria-label="Primary"
      style={{
        width: collapsed ? 'var(--w-sidebar-collapsed)' : 'var(--w-sidebar)',
      }}
      className={[
        'relative z-[var(--z-sticky)] flex h-full shrink-0 flex-col',
        'border-r border-line bg-[var(--canvas)]',
        'transition-[width] duration-[var(--dur-normal)] ease-[var(--ease-out)]',
      ].join(' ')}
    >
      {/* Brand. Sits on the same hairline as the top bar. */}
      <div
        className={[
          'flex h-[var(--h-topbar)] shrink-0 items-center border-b border-line',
          collapsed ? 'justify-center px-2' : 'gap-2.5 px-3',
        ].join(' ')}
      >
        <AlphaMark size={collapsed ? 24 : 26} className="shrink-0" />
        {collapsed ? null : (
          <span className="flex min-w-0 flex-col leading-none">
            <span className="truncate text-base font-semibold tracking-[var(--tracking-tight)] text-primary">
              AlphaForm
            </span>
            <span className="eyebrow mt-1">Group CRM</span>
          </span>
        )}
      </div>

      <ScrollArea className="min-h-0 flex-1" viewportClassName="px-2 py-2">
        {/* `data-tour` is the onboarding tour's anchor for "the left rail is the
            whole app". See `features/onboarding/TourOverlay`. */}
        <nav data-tour="nav" aria-label="Sections" className="flex flex-col gap-px">
          {PRIMARY_NAV.map((entry) => (
            <NavRow
              key={entry.id}
              to={entry.to}
              icon={entry.icon}
              label={entry.label}
              collapsed={collapsed}
              active={onPath(entry.to) && !activeView}
              count={
                entry.count === 'unread' ? unread : entry.count === 'overdue' ? overdue : undefined
              }
              /*
                Both section badges are EXCEPTION counts, so both wear the
                exception treatment. `Inbox 47` is the untriaged subset and
                `Tasks 12` the overdue one, while every row in VIEWS under them
                is a rows-in-view total — and the list `Inbox` opens says `913
                inquiries` in its own toolbar. Printed in the views' grey, `47`
                read as "47 inquiries" and quietly contradicted the surface it
                links to; the only thing saying otherwise was an `sr-only`
                prefix, i.e. an accessibility answer to a visual problem. The
                red ink is the visual half of that answer: one grammar for
                totals, another for what is waiting on you.
              */
              urgent={entry.count === 'overdue' || entry.count === 'unread'}
              countNoun={entry.count === 'overdue' ? 'overdue' : 'untriaged'}
              badgeWhenCollapsed
            />
          ))}
        </nav>

        {showViews ? (
          <div className="mt-4">
            {collapsed ? (
              <div aria-hidden="true" className="mx-auto mb-2 h-px w-6 bg-[var(--line)]" />
            ) : (
              <SectionLabel
                action={
                  /* The SAME create the shell's `New → View` and ⌘K's
                     `Create → View` fire — `useCreate('view')` — not a
                     navigation to the page that lists views. It used to land on
                     Settings › Saved views, which manages existing views and
                     whose own "New view" then sent you on to the inbox: two hops
                     and three different behaviours behind one word. */
                  canSaveView ? (
                    <IconButton
                      size="sm"
                      variant="ghost"
                      label="New view"
                      tooltipSide="right"
                      onClick={() => create('view')}
                    >
                      <Plus />
                    </IconButton>
                  ) : undefined
                }
              >
                Saved views
              </SectionLabel>
            )}

            <nav aria-label="Saved views" className="mt-0.5 flex flex-col gap-px">
              {views.isPending ? (
                <ViewSkeleton collapsed={collapsed} />
              ) : scopedViews.length === 0 ? (
                collapsed ? null : (
                  /* An instruction only where it can be followed. "Save a filtered
                   list to pin it here" was printed on surfaces with no save
                   control anywhere in their toolbar, and the only thing to press
                   discarded the filters it had just told the reader to save. */
                  <p className="px-2 py-1 text-xs leading-snug text-faint">
                    {canSaveView
                      ? `No ${viewNoun} views yet. Save a filtered list to pin it here.`
                      : surfaceEntity
                        ? `Saved ${viewNoun} views appear here.`
                        : 'Saved views appear here.'}
                  </p>
                )
              ) : (
                scopedViews.map((view) => (
                  <ViewRow
                    key={view.id}
                    view={view}
                    collapsed={collapsed}
                    active={activeView === view.id && onPath(VIEW_ROUTE[view.entity])}
                  />
                ))
              )}
            </nav>
          </div>
        ) : null}
      </ScrollArea>

      <div className="shrink-0 border-t border-line-subtle px-2 py-2">
        {/*
          Getting started sits with Settings rather than in `PRIMARY_NAV`,
          because the six rows above are collections of records and this is not
          one: it is the product explaining itself. Its badge counts STEPS LEFT,
          which is the same grammar the Inbox and Tasks badges use — a number
          beside a rail row means "this many things are waiting on you" — and it
          is `--text-muted` rather than red, because nothing here is overdue.
          Zero is silence: the row stays, the count goes.
        */}
        <NavRow
          to={ROUTES.gettingStarted}
          icon={LifeBuoy}
          label="Getting started"
          collapsed={collapsed}
          active={onPath(ROUTES.gettingStarted)}
          /* Silent until the three server-backed steps have answered. Painting
             `6` on the first frame and `2` two hundred milliseconds later is a
             rail that tells the reader they have done nothing, and then takes it
             back — the same defect the `unread` badge avoids by only ever
             printing a resolved total. */
          count={
            onboarding.nudge && !onboarding.loading
              ? onboarding.total - onboarding.done
              : undefined
          }
          countNoun="steps left"
        />
        {/*
          Bug triage, for administrators only. It sits here rather than in
          `PRIMARY_NAV` for the same reason Getting started does: the six rows
          above are collections of customer records and this is a queue about
          the product itself.

          The row is rendered on the admin's role rather than on the count being
          non-zero, because a queue you can only see when it is full is a queue
          nobody checks. The badge counts reports that are OPEN AND UNVERIFIED —
          the gap between "someone shipped a fix" and "someone confirmed it" —
          which is the same "this many things are waiting on you" grammar the
          Inbox and Tasks badges use. Zero is silence: the row stays, the count
          goes.
        */}
        {session?.user.role === 'admin' ? (
          <NavRow
            to={ROUTES.bugs}
            icon={Bug}
            label="Bug reports"
            collapsed={collapsed}
            active={onPath(ROUTES.bugs)}
            count={unverifiedBugs.data}
            countNoun="unverified reports"
          />
        ) : null}
        <NavRow
          to={ROUTES.settings}
          icon={Settings}
          label="Settings"
          collapsed={collapsed}
          active={onPath(ROUTES.settings)}
        />
      </div>

      <div className="shrink-0 border-t border-line-subtle p-2">
        <UserMenu session={session} variant={collapsed ? 'avatar' : 'row'} side="top" />
      </div>
    </aside>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   The same rail, as a drawer
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Below `MOBILE_BREAKPOINT` the rail is not a column of the layout, it is a
 * panel over it: 232px of a 390px screen is most of the reading width, and the
 * 56px collapsed state trades the labels away and still keeps the pixels.
 *
 * It is the *same* `<Sidebar/>`, always expanded — a rail you had to open on
 * purpose has no reason to hide its labels — inside a modal so the shortcuts,
 * the scroll lock and the focus trap come from Radix rather than from us.
 *
 * It closes on navigation. The whole point of opening it is to go somewhere,
 * and a drawer still sitting over the screen you just asked for is a second
 * gesture the reader did not ask to make. The effect keys on the location, so
 * it fires on the navigation and not on the open.
 */
export function NavDrawer({
  open,
  onOpenChange,
  session,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: SessionResponse | undefined;
}) {
  const location = useLocation();
  const key = `${location.pathname}${location.search}`;

  useEffect(() => {
    onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the location IS the trigger
  }, [key]);

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {/* React hoists this into <head> once, however many times it renders. */}
      <style href="af-drawer" precedence="component">
        {DRAWER_CSS}
      </style>
      <DialogPortal>
        <DialogOverlay className={dialogOverlayClass} />
        <DialogContent
          aria-describedby={undefined}
          className={[
            'fixed inset-y-0 left-0 z-[var(--z-modal)] flex max-w-[86vw] flex-col',
            'shadow-[var(--shadow-4)] focus:outline-none',
            'data-[state=open]:animate-[af-sheet-in-left_var(--dur-normal)_var(--ease-out)]',
            'data-[state=closed]:animate-[af-sheet-out-left_var(--dur-fast)_var(--ease-in-out)]',
          ].join(' ')}
          style={{ width: 'var(--w-sidebar)' }}
        >
          <DialogTitle className="sr-only">Navigation</DialogTitle>
          {/* The rail paints its own ground, border and full height. */}
          <Sidebar collapsed={false} session={session} />
          {/* Off-screen, so Esc and the overlay are not the only ways out for a
              reader who navigates by control rather than by gesture. */}
          <DialogClose asChild>
            <button type="button" className="sr-only">
              Close navigation
            </button>
          </DialogClose>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}

/** Keyframes for the drawer's travel; `Sheet` hoists the same pair for its own. */
const DRAWER_CSS = `
@keyframes af-sheet-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
@keyframes af-sheet-out-left { from { transform: translateX(0); } to { transform: translateX(-100%); } }
`;

function ViewSkeleton({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="flex flex-col gap-px" aria-hidden="true">
      {[62, 78, 54].map((width) => (
        <div
          key={width}
          className={[
            'flex items-center',
            collapsed ? 'h-9 w-9 justify-center' : 'h-[30px] gap-2 px-2',
          ].join(' ')}
        >
          <Skeleton w={16} h={16} rounded="xs" />
          {collapsed ? null : <Skeleton w={width} h={9} rounded="xs" />}
        </div>
      ))}
    </div>
  );
}

/** Chrome-only rail, shown while the session is still resolving. */
export function SidebarSkeleton({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <aside
      aria-hidden="true"
      style={{
        width: collapsed ? 'var(--w-sidebar-collapsed)' : 'var(--w-sidebar)',
      }}
      className="flex h-full shrink-0 flex-col border-r border-line bg-[var(--canvas)]"
    >
      <div className="flex h-[var(--h-topbar)] shrink-0 items-center gap-2.5 border-b border-line px-3">
        <AlphaMark size={26} />
        {collapsed ? null : (
          <span className="flex flex-col gap-1.5">
            <Skeleton w={72} h={10} rounded="xs" />
            <Skeleton w={46} h={7} rounded="xs" />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-px px-2 py-2">
        {[74, 58, 82, 90, 62, 78].map((width) => (
          <div key={width} className="flex h-[30px] items-center gap-2 px-2">
            <Skeleton w={16} h={16} rounded="xs" />
            {collapsed ? null : <Skeleton w={width} h={9} rounded="xs" />}
          </div>
        ))}
      </div>
    </aside>
  );
}
